import { useState, useCallback, useMemo } from 'react';
import type { TrendStatus } from '@ongrid/shared';
import { useOverview, usePages, usePageDetail } from './lib/hooks';
import { formatNumber, formatPosition } from './lib/utils';
import { StatCard } from './components/StatCard';
import { TrendChart } from './components/TrendChart';
import { PageTable } from './components/PageTable';
import { PageDetail } from './components/PageDetail';
import { FilterBar } from './components/FilterBar';
import { Pagination } from './components/Pagination';

function App() {
  // State
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [sortBy, setSortBy] = useState('last_30d_clicks');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [trendStatus, setTrendStatus] = useState<TrendStatus | null>(null);
  const [search, setSearch] = useState('');
  const [selectedPageUrl, setSelectedPageUrl] = useState<string | null>(null);

  // Data fetching
  const { data: overview, loading: overviewLoading, refetch: refetchOverview } = useOverview();
  const pagesParams = useMemo(
    () => ({ page, pageSize, sortBy, sortOrder, trendStatus, search }),
    [page, pageSize, sortBy, sortOrder, trendStatus, search]
  );
  const {
    data: pages,
    total,
    loading: pagesLoading,
    refetch: refetchPages,
  } = usePages(pagesParams);
  const { summary, history, loading: detailLoading } = usePageDetail(selectedPageUrl);

  // Handlers
  const handleSort = useCallback((column: string) => {
    setSortBy((prev) => {
      if (prev === column) {
        setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'));
        return column;
      }
      setSortOrder('desc');
      return column;
    });
    setPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    refetchOverview();
    refetchPages();
  }, [refetchOverview, refetchPages]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleTrendStatusChange = useCallback((value: TrendStatus | null) => {
    setTrendStatus(value);
    setPage(1);
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-semibold">GSC Dashboard</h1>
                <p className="text-sm text-[var(--text-muted)]">ongrid.design</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-[var(--text-muted)]">Last sync</p>
              <p className="text-sm text-[var(--text-secondary)]">
                {overview?.totalPages ? 'Data available' : 'No data yet'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview section */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Overview</h2>
          {overviewLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="h-28 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl animate-pulse"
                />
              ))}
            </div>
          ) : overview ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Total Pages"
                value={formatNumber(overview.totalPages)}
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                }
              />
              <StatCard
                title="Total Clicks (30d)"
                value={formatNumber(overview.totalClicks30d)}
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                    />
                  </svg>
                }
              />
              <StatCard
                title="Total Impressions (30d)"
                value={formatNumber(overview.totalImpressions30d)}
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                }
              />
              <StatCard
                title="Avg Position"
                value={formatPosition(overview.avgPosition)}
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                }
              />
            </div>
          ) : (
            <div className="text-center py-8 text-[var(--text-muted)]">
              Failed to load overview data
            </div>
          )}
        </section>

        {/* Trend distribution */}
        {overview && (
          <section className="mb-8">
            <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">Trend Distribution</h2>
              <TrendChart distribution={overview.trendDistribution} />
            </div>
          </section>
        )}

        {/* Pages table */}
        <section>
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4">All Pages</h2>

            <FilterBar
              search={search}
              onSearchChange={handleSearchChange}
              trendStatus={trendStatus}
              onTrendStatusChange={handleTrendStatusChange}
              onRefresh={handleRefresh}
            />

            <PageTable
              data={pages}
              loading={pagesLoading}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              onRowClick={setSelectedPageUrl}
            />

            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
            />
          </div>
        </section>
      </main>

      {/* Page detail modal */}
      {selectedPageUrl && summary && (
        <PageDetail
          summary={summary}
          history={history}
          onClose={() => setSelectedPageUrl(null)}
        />
      )}

      {/* Loading overlay for detail */}
      {selectedPageUrl && detailLoading && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="animate-pulse text-[var(--text-secondary)]">Loading page details...</div>
        </div>
      )}
    </div>
  );
}

export default App;

